import _objectSpread from "/usr/app/node_modules/@babel/runtime/helpers/esm/objectSpread2.js";
import _slicedToArray from "/usr/app/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import React from "react";
var __jsx = React.createElement;
import { useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Icon } from "@syarahtech/ui";
import { useTranslation } from "@i18n";
import { useLang } from "@/common/Contexts/LanguageContext";
import { CDN_LINK } from "@globalConfig";
import useApi from "@hooks/useApi";
import useButtonLoading from "@hooks/useButtonLoading";
import useIsMobile from "@hooks/useIsMobile";
import { TextField } from "@/components/ui/text-field";
import { PhoneField, toCanonicalPhone, toNationalPhone } from "@/components/ui/phone-field";
import { SelectField } from "@/components/ui/select-field";
import MobileCarInfoPhotoV2 from "@/pages/checkout/components/HeadingSteps/MobileCarInfo/MobileCarInfoPhotoV2";
import { api__getCitiesDropDown } from "@/apis/assets/api__get_cities_drop_down";
import { useAuthActions, useSession } from "@/_MiniApps/auth/AuthContext";
import ChangeAccountVerificationModal from "@/_MiniApps/auth/ui/ChangeAccountVerificationModal/ChangeAccountVerificationModal";
import { useCreditCardCheckout } from "@/common/Contexts/CreditCardCheckoutContext";
import { RoadsideAssistanceOption, findRoadServiceSection, getDefaultRoadServiceItem } from "@/_MiniApps/RoadsideAssistance";
import RiyalSymbol from "@componentsShared/InputCurrency/RiyalSymbol";
import { toast } from "sonner";
import { showAppToast } from "@/components/ui/app-toast";
import { trackEvents } from "@/common/events/events";
import { getUtmParams } from "@/common/utmParams";
var NAME_PATTERN = /^[؀-ۿa-zA-Z\s]+$/;
var schema = z.object({
  first_name: z.string().min(1, "validation_name_filled").regex(NAME_PATTERN, "validation_name_contain_symbols_unsupported_characters"),
  family_name: z.string().min(1, "validation_name_filled").regex(NAME_PATTERN, "validation_name_contain_symbols_unsupported_characters"),
  phone_number: z.string().min(1, "validation_mobile_number_chosen").regex(/^(05)[0-9]{8}$/, "validation_mobile_number_consist_ten_numbers"),
  city_id: z.string().min(1, "validation_city_chosen"),
  whatsapp_optin: z["boolean"]().optional()
});
var splitFullName = function splitFullName(fullName) {
  var parts = (fullName || "").trim().split(/\s+/).filter(Boolean);
  return {
    first_name: parts[0] || "",
    family_name: parts.slice(1).join(" ") || ""
  };
};

// credit-card has its OWN persist key (NOT the cash/shop-apply key) so warranty/city/name
// are independent per flow; context mount-restore reads the same key on credit-card pages.
var getStorageKey = function getStorageKey(postId) {
  return "credit_card_form_data_".concat(postId);
};
var readSession = function readSession(postId) {
  try {
    var raw = typeof sessionStorage !== "undefined" ? sessionStorage.getItem(getStorageKey(postId)) : null;
    return raw ? JSON.parse(raw) : {};
  } catch (_unused) {
    return {};
  }
};

// floating label chip sitting on the input top border (desktop only — matches Figma)
var FloatingLabel = function FloatingLabel(_ref) {
  var children = _ref.children;
  return /*#__PURE__*/React.createElement("span", {
    className: "hidden md:block absolute -top-2 right-3 bg-white px-1 text-[12px] font-semibold leading-none text-[#020202]"
  }, children);
};
var CreditCardForm = function CreditCardForm(_ref2) {
  var _roadServiceSection$s3, _ref4, _sessionData$first_na, _ref5, _sessionData$family_n, _formData$whatsapp_op, _checkoutOrderV2$deta2, _checkoutOrderV2$deta3, _checkoutOrderV2$deta4, _checkoutOrderV2$deta5;
  var onComplete = _ref2.onComplete;
  var _useTranslation = useTranslation(["web_checkout_cash", "web_checkout_finance"]),
    t = _useTranslation.t,
    tShort = _useTranslation.tShort;
  var lang = useLang();
  var buttonLoading = useButtonLoading();
  var _useSession = useSession(),
    isLoggedIn = _useSession.isLoggedIn,
    user = _useSession.user;
  var _useAuthActions = useAuthActions(),
    logout = _useAuthActions.logout;
  var _useState = useState(false),
    showVerificationModal = _useState[0],
    setShowVerificationModal = _useState[1];
  var _useCreditCardCheckou = useCreditCardCheckout().state,
    checkoutOrderV2 = _useCreditCardCheckou.checkoutOrderV2,
    formData = _useCreditCardCheckou.formData,
    fetching_error = _useCreditCardCheckou.fetching_error;
  var post_id = useCreditCardCheckout().queryParams.post_id;
  var setFormItem = useCreditCardCheckout().actions.setFormItem;
  var fetch__getOrderDetailsByParamsV2 = useCreditCardCheckout().remote_actions.fetch__getOrderDetailsByParamsV2;
  var _useApi = useApi([], api__getCitiesDropDown),
    _useApi2 = _slicedToArray(_useApi, 3),
    cities = _useApi2[2];

  // Roadside Assistance service (SE-12695). Content + toggle text/state from the API.
  // SE-12880: seed from the stored session choice so the toggle survives back-navigation.
  var roadServiceSection = findRoadServiceSection(checkoutOrderV2 === null || checkoutOrderV2 === void 0 ? void 0 : checkoutOrderV2.sections);
  var roadServiceItem = getDefaultRoadServiceItem(roadServiceSection);
  var _useState2 = useState(function () {
      var stored = readSession(post_id).road_service_id;
      return stored != null ? Number(stored) !== 0 : true;
    }),
    roadsideEnabled = _useState2[0],
    setRoadsideEnabled = _useState2[1];
  useEffect(function () {
    var _ref3, _roadServiceSection$s, _roadServiceSection$s2, _roadServiceItem$id;
    if (!roadServiceItem) return;
    var on = (_ref3 = (_roadServiceSection$s = roadServiceSection === null || roadServiceSection === void 0 ? void 0 : (_roadServiceSection$s2 = roadServiceSection.selected_item) === null || _roadServiceSection$s2 === void 0 ? void 0 : _roadServiceSection$s2.toggle) !== null && _roadServiceSection$s !== void 0 ? _roadServiceSection$s : roadServiceItem.selected) !== null && _ref3 !== void 0 ? _ref3 : false;
    setRoadsideEnabled(on);
    setFormItem("road_service_id", on ? (_roadServiceItem$id = roadServiceItem.id) !== null && _roadServiceItem$id !== void 0 ? _roadServiceItem$id : 0 : 0);
  }, [roadServiceSection === null || roadServiceSection === void 0 ? void 0 : (_roadServiceSection$s3 = roadServiceSection.selected_item) === null || _roadServiceSection$s3 === void 0 ? void 0 : _roadServiceSection$s3.toggle, roadServiceItem === null || roadServiceItem === void 0 ? void 0 : roadServiceItem.id, roadServiceItem === null || roadServiceItem === void 0 ? void 0 : roadServiceItem.selected]);
  var persistRoadServiceId = function persistRoadServiceId(rsid) {
    try {
      if (typeof sessionStorage === "undefined") return;
      var existing = readSession(post_id);
      sessionStorage.setItem(getStorageKey(post_id), JSON.stringify(_objectSpread(_objectSpread({}, existing), {}, {
        road_service_id: rsid
      })));
    } catch (_unused2) {
      // sessionStorage full or unavailable
    }
  };
  var handleRoadsideToggle = function handleRoadsideToggle(next) {
    var _roadServiceItem$id2;
    setRoadsideEnabled(next);
    var rsid = next ? (_roadServiceItem$id2 = roadServiceItem === null || roadServiceItem === void 0 ? void 0 : roadServiceItem.id) !== null && _roadServiceItem$id2 !== void 0 ? _roadServiceItem$id2 : 0 : 0;
    setFormItem("road_service_id", rsid);
    persistRoadServiceId(rsid);
    fetch__getOrderDetailsByParamsV2(post_id, _objectSpread(_objectSpread({
      road_service_id: rsid
    }, (formData === null || formData === void 0 ? void 0 : formData.city_id) != null && {
      city_id: Number(formData.city_id)
    }), (formData === null || formData === void 0 ? void 0 : formData.warranty_id) != null && {
      warranty_id: formData.warranty_id
    }));
  };

  // restore on back-navigation from Arboon: session → context → user
  var sessionData = readSession(post_id);
  var sessionNames = splitFullName(sessionData.full_name);
  var prefill = splitFullName((formData === null || formData === void 0 ? void 0 : formData.full_name) || (user === null || user === void 0 ? void 0 : user.full_name));
  var methods = useForm({
    mode: "onBlur",
    resolver: zodResolver(schema),
    defaultValues: {
      first_name: (_ref4 = (_sessionData$first_na = sessionData.first_name) !== null && _sessionData$first_na !== void 0 ? _sessionData$first_na : sessionNames.first_name) !== null && _ref4 !== void 0 ? _ref4 : prefill.first_name,
      family_name: (_ref5 = (_sessionData$family_n = sessionData.family_name) !== null && _sessionData$family_n !== void 0 ? _sessionData$family_n : sessionNames.family_name) !== null && _ref5 !== void 0 ? _ref5 : prefill.family_name,
      phone_number: (formData === null || formData === void 0 ? void 0 : formData.phone_number) || (user === null || user === void 0 ? void 0 : user.phone_number) || "",
      city_id: sessionData.city_id != null ? String(sessionData.city_id) : formData !== null && formData !== void 0 && formData.city_id ? String(formData.city_id) : "",
      whatsapp_optin: sessionData.whatsapp_optin != null ? Number(sessionData.whatsapp_optin) !== 0 : Number((_formData$whatsapp_op = formData === null || formData === void 0 ? void 0 : formData.whatsapp_optin) !== null && _formData$whatsapp_op !== void 0 ? _formData$whatsapp_op : 1) !== 0
    }
  });
  var control = methods.control,
    handleSubmit = methods.handleSubmit,
    getValues = methods.getValues,
    errors = methods.formState.errors;
  var err = function err(field) {
    var _errors$field;
    return (_errors$field = errors[field]) !== null && _errors$field !== void 0 && _errors$field.message ? t(errors[field].message, {
      ns: "web_checkout_cash"
    }) : "";
  };

  // SE: restore saved selections into the shared context on mount (provider remounts on back-nav,
  // wiping formData) so the CTA city/warranty and the create payload stay correct.
  useEffect(function () {
    var s = readSession(post_id);
    if (s.full_name != null) setFormItem("full_name", s.full_name);
    if (s.city_id != null) setFormItem("city_id", Number(s.city_id));
    if (s.warranty_id != null) setFormItem("warranty_id", Number(s.warranty_id));
    if (s.road_service_id != null) setFormItem("road_service_id", Number(s.road_service_id));
  }, []);

  // persist form fields on every change so back-navigation from Arboon restores them
  useEffect(function () {
    var sub = methods.watch(function (values) {
      try {
        var _values$first_name, _values$family_name;
        if (typeof sessionStorage === "undefined") return;
        var key = getStorageKey(post_id);
        var existing = readSession(post_id);
        var first = (values.first_name || "").trim();
        var family = (values.family_name || "").trim();
        sessionStorage.setItem(key, JSON.stringify(_objectSpread(_objectSpread(_objectSpread({}, existing), {}, {
          first_name: (_values$first_name = values.first_name) !== null && _values$first_name !== void 0 ? _values$first_name : "",
          family_name: (_values$family_name = values.family_name) !== null && _values$family_name !== void 0 ? _values$family_name : "",
          full_name: [first, family].filter(Boolean).join(" ")
        }, values.city_id ? {
          city_id: values.city_id
        } : {}), {}, {
          whatsapp_optin: values.whatsapp_optin ? 1 : 0
        })));
      } catch (_unused3) {
        // sessionStorage full or unavailable
      }
    });
    return function () {
      return sub.unsubscribe();
    };
  }, [post_id]);

  // surface pricing fetch errors
  useEffect(function () {
    if (fetching_error !== null && fetching_error !== void 0 && fetching_error.message) toast.error(fetching_error.message, {
      position: "bottom-center"
    });
  }, [fetching_error]);
  var buildPayload = function buildPayload(data) {
    return _objectSpread(_objectSpread({}, getUtmParams()), {}, {
      post_id: post_id,
      first_name: data.first_name.trim(),
      last_name: data.family_name.trim(),
      phone_number: data.phone_number,
      city_id: Number(data.city_id),
      whatsapp_optin: data.whatsapp_optin ? 1 : 0
    });
  };

  // SE-13409: the toast is the "you haven't filled anything in" prompt. Once any
  // required field carries a value the inline field errors say what is wrong, so
  // stacking a toast on top of them is noise.
  var onInvalid = function onInvalid() {
    var _getValues = getValues(),
      first_name = _getValues.first_name,
      family_name = _getValues.family_name,
      phone_number = _getValues.phone_number,
      city_id = _getValues.city_id;
    var allRequiredEmpty = [first_name, family_name, phone_number, city_id].every(function (value) {
      return !String(value !== null && value !== void 0 ? value : "").trim() || String(value).trim() === "0";
    });
    if (!allRequiredEmpty) return;
    showAppToast(tShort("flexible_payment/toast_fill_fields", {
      ns: "web_checkout_finance"
    }), {
      id: "credit-card-fill-fields",
      className: "md:mt-[80px]"
    });
  };
  var onSubmit = handleSubmit(function (data) {
    var _checkoutOrderV2$g4_d, _checkoutOrderV2$deta;
    // shop-apply / amwal parity: a logged-in user entering a phone different from their
    // account must confirm, then re-verify the new number via OTP.
    if (isLoggedIn && (user === null || user === void 0 ? void 0 : user.phone_number) != data.phone_number && !showVerificationModal) {
      return setShowVerificationModal(true);
    }
    var g4 = checkoutOrderV2 === null || checkoutOrderV2 === void 0 ? void 0 : (_checkoutOrderV2$g4_d = checkoutOrderV2.g4_data) === null || _checkoutOrderV2$g4_d === void 0 ? void 0 : _checkoutOrderV2$g4_d.g4_data_layer;
    var selectedCity = (cities || []).find(function (c) {
      return String(c.id) === data.city_id;
    });
    trackEvents("credit_card_form_submit", {
      post_id: String(post_id),
      inventory_type: g4 === null || g4 === void 0 ? void 0 : g4.inventory_type,
      post_make: g4 === null || g4 === void 0 ? void 0 : g4.post_make,
      post_model: g4 === null || g4 === void 0 ? void 0 : g4.post_model,
      post_year: g4 === null || g4 === void 0 ? void 0 : g4.post_year,
      post_condition: g4 === null || g4 === void 0 ? void 0 : g4.post_condition,
      post_price: g4 === null || g4 === void 0 ? void 0 : g4.post_price,
      total_amount: checkoutOrderV2 === null || checkoutOrderV2 === void 0 ? void 0 : (_checkoutOrderV2$deta = checkoutOrderV2.detail) === null || _checkoutOrderV2$deta === void 0 ? void 0 : _checkoutOrderV2$deta.total_amount,
      user_city: selectedCity === null || selectedCity === void 0 ? void 0 : selectedCity.name,
      platform: "web",
      referral_location: "pre_checkout"
    });
    // not logged in → OTP first; logged-in with own number → straight through.
    // (Order is created by the page after OTP — the create endpoint needs auth.)
    onComplete(buildPayload(data), !isLoggedIn);
  }, onInvalid);
  var isMobile = useIsMobile();
  var cardClass = isMobile ? "border border-solid border-[#e1e8f2] bg-white px-4 py-5" : "";
  var getErrMsg = function getErrMsg(field) {
    var error = methods.formState.errors[field];
    return error ? t(error.message, {
      ns: "web_checkout_cash"
    }) : "";
  };
  return /*#__PURE__*/React.createElement(FormProvider, methods, /*#__PURE__*/React.createElement("form", {
    onSubmit: onSubmit,
    noValidate: true,
    autoComplete: "off",
    className: cardClass
  }, /*#__PURE__*/React.createElement("h1", {
    className: "py-2 text-base font-bold text-[#00143c] md:py-5 md:text-xl mb-[34px]"
  }, t("add_information", {
    ns: "web_checkout_cash"
  })), /*#__PURE__*/React.createElement("div", {
    className: "mb-[34px]"
  }, /*#__PURE__*/React.createElement(Controller, {
    name: "first_name",
    control: control,
    render: function render(_ref6) {
      var field = _ref6.field;
      return /*#__PURE__*/React.createElement(TextField, {
        id: "credit-card-firstname-input",
        required: true,
        label: tShort("flexible_payment/first_name_label", {
          ns: "web_checkout_finance"
        }),
        placeholder: t("first_name", {
          ns: "web_checkout_cash"
        }),
        value: field.value,
        onBlur: field.onBlur,
        error: getErrMsg("first_name"),
        onChange: function onChange(event) {
          field.onChange(event);
          setFormItem("first_name", event.target.value);
        }
      });
    }
  })), /*#__PURE__*/React.createElement("div", {
    className: "mb-[34px]"
  }, /*#__PURE__*/React.createElement(Controller, {
    name: "family_name",
    control: control,
    render: function render(_ref7) {
      var field = _ref7.field;
      return /*#__PURE__*/React.createElement(TextField, {
        id: "credit-card-familyname-input",
        required: true,
        label: tShort("flexible_payment/family_name_label", {
          ns: "web_checkout_finance"
        }),
        placeholder: t("family_name", {
          ns: "web_checkout_cash"
        }),
        value: field.value,
        onBlur: field.onBlur,
        error: getErrMsg("family_name"),
        onChange: function onChange(event) {
          field.onChange(event);
          setFormItem("family_name", event.target.value);
        }
      });
    }
  })), /*#__PURE__*/React.createElement("div", {
    className: "mb-[34px]"
  }, /*#__PURE__*/React.createElement(Controller, {
    name: "phone_number",
    control: control,
    render: function render(_ref8) {
      var field = _ref8.field;
      return /*#__PURE__*/React.createElement(PhoneField, {
        id: "credit-card-phone-input",
        required: true,
        maxDigits: 9,
        label: tShort("flexible_payment/phone_label", {
          ns: "web_checkout_finance"
        }),
        value: toNationalPhone(field.value),
        onChange: function onChange(nextValue) {
          var canonical = toCanonicalPhone(nextValue);
          field.onChange(canonical);
          setFormItem("phone_number", canonical);
        },
        onBlur: field.onBlur,
        error: getErrMsg("phone_number")
      });
    }
  }), /*#__PURE__*/React.createElement("label", {
    id: "credit-card-whatsapp-label",
    className: "mt-3 flex cursor-pointer items-center gap-1"
  }, /*#__PURE__*/React.createElement(Controller, {
    name: "whatsapp_optin",
    control: control,
    render: function render(_ref9) {
      var field = _ref9.field;
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("input", {
        type: "checkbox",
        className: "peer sr-only",
        checked: !!field.value,
        onChange: function onChange(e) {
          field.onChange(e.target.checked ? 1 : 0);
          setFormItem("whatsapp_optin", e.target.checked ? 1 : 0);
        }
      }), /*#__PURE__*/React.createElement("span", {
        className: "flex h-[17px] w-[17px] shrink-0 items-center justify-center rounded-[4px] border border-solid border-primary-new text-transparent peer-checked:text-primary-new"
      }, /*#__PURE__*/React.createElement(Icon, {
        icon: "material-symbols--check-rounded",
        width: 3.5,
        height: 3.5
      })));
    }
  }), /*#__PURE__*/React.createElement("img", {
    src: CDN_LINK + "/assets/images/wpImg.svg",
    alt: "whts",
    width: 16,
    height: 16
  }), /*#__PURE__*/React.createElement("span", {
    className: "text-xs leading-6 text-[#6c7a8d]"
  }, t("like_receive_updates_order_via", {
    ns: "web_checkout_finance"
  })))), /*#__PURE__*/React.createElement("div", {
    className: "mb-[34px]"
  }, /*#__PURE__*/React.createElement(Controller, {
    name: "city_id",
    control: control,
    render: function render(_ref0) {
      var field = _ref0.field;
      return /*#__PURE__*/React.createElement(SelectField, {
        id: "credit-card-city-select",
        required: true,
        label: tShort("flexible_payment/city_label", {
          ns: "web_checkout_finance"
        }),
        placeholder: t("city", {
          ns: "web_checkout_cash"
        }),
        value: field.value || "",
        error: getErrMsg("city_id"),
        options: (cities || []).map(function (city) {
          return {
            value: String(city.id),
            label: city.name
          };
        }),
        sheetTitle: t("city", {
          ns: "web_checkout_cash"
        }),
        searchPlaceholder: t("city", {
          ns: "web_checkout_cash"
        }),
        onChange: function onChange(value) {
          field.onChange(value);
          setFormItem("city_id", Number(value));
          fetch__getOrderDetailsByParamsV2(post_id, _objectSpread({
            city_id: Number(value)
          }, (formData === null || formData === void 0 ? void 0 : formData.warranty_id) != null && {
            warranty_id: formData.warranty_id
          }));
        }
      });
    }
  })), roadServiceItem && /*#__PURE__*/React.createElement("div", {
    className: "mb-[34px]"
  }, /*#__PURE__*/React.createElement(RoadsideAssistanceOption, {
    checked: roadsideEnabled,
    onChange: handleRoadsideToggle,
    item: roadServiceItem
  })), isMobile ? /*#__PURE__*/React.createElement("div", {
    id: "credit-card-mobile-submit-wrapper",
    className: "fixed bottom-0 left-0 z-[1] w-full rounded-t-2xl bg-white shadow-[0px_-1px_2px_0px_rgba(0,0,0,0.08)]"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-6 p-4 pb-6"
  }, /*#__PURE__*/React.createElement(MobileCarInfoPhotoV2, {
    checkoutOrderV2: checkoutOrderV2,
    onClickInfoIcon: function onClickInfoIcon() {}
  }), /*#__PURE__*/React.createElement("button", {
    id: "credit-card-submit-btn",
    type: "submit",
    disabled: buttonLoading,
    className: "flex h-14 w-full items-center justify-center gap-3 rounded-md border-none bg-primary-new px-3 py-4 text-base font-bold text-white transition-colors hover:bg-primary-120 disabled:bg-[#E8EFF6] disabled:text-[#92A5C1] disabled:opacity-100"
  }, /*#__PURE__*/React.createElement("strong", null, t("continue_down_payment", {
    ns: "web_checkout_cash"
  })), (checkoutOrderV2 === null || checkoutOrderV2 === void 0 ? void 0 : (_checkoutOrderV2$deta2 = checkoutOrderV2.detail) === null || _checkoutOrderV2$deta2 === void 0 ? void 0 : _checkoutOrderV2$deta2.arboun) && /*#__PURE__*/React.createElement("strong", {
    className: "whitespace-nowrap"
  }, checkoutOrderV2 === null || checkoutOrderV2 === void 0 ? void 0 : (_checkoutOrderV2$deta3 = checkoutOrderV2.detail) === null || _checkoutOrderV2$deta3 === void 0 ? void 0 : _checkoutOrderV2$deta3.arboun, " ", /*#__PURE__*/React.createElement(RiyalSymbol, {
    className: "inline text-white text-base font-bold"
  }))))) : /*#__PURE__*/React.createElement("div", {
    id: "credit-card-desktop-submit-wrapper",
    className: "mt-8"
  }, /*#__PURE__*/React.createElement("button", {
    id: "credit-card-submit-btn",
    type: "submit",
    disabled: buttonLoading,
    className: "flex h-14 w-full items-center justify-center gap-3 rounded-md border-none bg-primary-new px-3 py-4 text-base font-bold text-white transition-colors hover:bg-primary-120 disabled:bg-[#E8EFF6] disabled:text-[#92A5C1] disabled:opacity-100"
  }, /*#__PURE__*/React.createElement("strong", null, t("continue_down_payment", {
    ns: "web_checkout_cash"
  })), (checkoutOrderV2 === null || checkoutOrderV2 === void 0 ? void 0 : (_checkoutOrderV2$deta4 = checkoutOrderV2.detail) === null || _checkoutOrderV2$deta4 === void 0 ? void 0 : _checkoutOrderV2$deta4.arboun) && /*#__PURE__*/React.createElement("strong", {
    className: "whitespace-nowrap"
  }, checkoutOrderV2 === null || checkoutOrderV2 === void 0 ? void 0 : (_checkoutOrderV2$deta5 = checkoutOrderV2.detail) === null || _checkoutOrderV2$deta5 === void 0 ? void 0 : _checkoutOrderV2$deta5.arboun, " ", /*#__PURE__*/React.createElement(RiyalSymbol, {
    className: "inline text-white text-base font-bold"
  }))))), /*#__PURE__*/React.createElement(ChangeAccountVerificationModal, {
    show: showVerificationModal,
    onClose: function onClose() {
      return setShowVerificationModal(false);
    },
    onSubmit: function onSubmit() {
      setShowVerificationModal(false);
      // log out the old account, then verify the new number via OTP before creating the order
      logout(null).then(function () {
        return onComplete(buildPayload(getValues()), true);
      });
    },
    newPhoneNumber: getValues("phone_number"),
    loading: buttonLoading
  }));
};
export default CreditCardForm;